import type { ReactNode } from "react";
import type React from "react";
import { useMemo, useEffect, useCallback, useRef } from "react";
import { useHelpDocsLighthouse } from "./useHelpDocsLighthouse";
import type { HelpDocsConfig, HelpDocsNavigateData } from "./useHelpDocsLighthouseTypes";
import { useTranslation } from "react-i18next";
import { useHelpLauncherBottom } from "./useHelpLauncherBottom";
import { useLocation, useNavigate } from "react-router-dom";
import { injectLighthousePanelStyles } from "./injectLighthousePanelStyles";
import "./lighthouse.scss";

const WIXARPEDIA_ARTICLE_ID = "taqg83nqwp";
const WIXARPEDIA_CHAT_ROUTE = "/chat/wixarpedia";

interface Ii18nStrings {
  contact_button: string;
  contact_name_placeholder: string;
  contact_email_placeholder: string;
  contact_message_placeholder: string;
  contact_submit: string;
  search_placeholder: string;
  view_all: string;
  suggested_articles: string;
}

interface Props {
  widgetKey: string;
  languageCode: string;
  configOverrides?: Omit<HelpDocsConfig, "widget_key" | "language_code" | "i18n">;
  children?: ReactNode;
}

export const HelpDocsLighthouseWidget: React.FC<Props> = ({
  widgetKey,
  languageCode,
  configOverrides = {},
  children,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const path = location.pathname.split("/")[1];
  const inProfile = path === "profile";

  const launcherBottom = useHelpLauncherBottom();

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (window.Intercom && !window.Intercom.isWixarpediaStub) {
      return;
    }
    const stub: NonNullable<Window["Intercom"]> = (command: unknown) => {
      if (typeof command === "string" && command.startsWith("show")) {
        window.Lighthouse?.hide?.();
        navigateRef.current(WIXARPEDIA_CHAT_ROUTE);
      }
    };
    stub.booted = true;
    stub.isWixarpediaStub = true;
    window.Intercom = stub;
  }, []);

  const onNavigate = useCallback((data: HelpDocsNavigateData) => {
    const isDecoyArticle = data.page === "article" && data.article_id === WIXARPEDIA_ARTICLE_ID;
    if (data.page === "contact" || isDecoyArticle) {
      window.Lighthouse?.hide?.();
      navigateRef.current(WIXARPEDIA_CHAT_ROUTE);
    }
  }, []);

  useEffect(() => {
    const host = document.getElementById("lighthouse");
    if (!host) return undefined;

    host.style.setProperty("--launcher-bottom", `${launcherBottom}px`);
    host.style.setProperty("--launcher-visibility", inProfile ? "hidden" : "visible");
    return undefined;
  }, [launcherBottom, inProfile]);

  const i18nStrings: Ii18nStrings = {
    contact_button: t("helpdocswidget.contact_button"),
    contact_name_placeholder: t("helpdocswidget.contact_name_placeholder"),
    contact_email_placeholder: t("helpdocswidget.contact_email_placeholder"),
    contact_message_placeholder: t("helpdocswidget.contact_message_placeholder"),
    contact_submit: t("helpdocswidget.contact_submit"),
    search_placeholder: t("helpdocswidget.search_placeholder"),
    view_all: t("helpdocswidget.view_all"),
    suggested_articles: t("helpdocswidget.suggested_articles"),
  };

  const config = useMemo<HelpDocsConfig>(
    () => ({
      widget_key: widgetKey,
      language_code: languageCode,
      launcher_button_image:
        "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/InformationRoundedSquareIcon.svg",
      i18n: i18nStrings,
      logo: "https://s3.eu-west-3.amazonaws.com/storage.wixar.io/Wixar_Editor_Logo_Capital_500x500.png",
      shared_password: "883e901b-481e-4af3-8941-17f755014cae",
      disable_authorship: true,
      onNavigate,
      onLoad: () => injectLighthousePanelStyles(),
      onShow: () => injectLighthousePanelStyles(),
      ...configOverrides,
    }),
    [
      widgetKey,
      languageCode,
      JSON.stringify(i18nStrings),
      JSON.stringify(configOverrides),
      onNavigate,
    ],
  );

  useHelpDocsLighthouse(config);

  return <>{children}</>;
};
